import React from 'react'
import { Box, Button, Checkbox, Stack, Typography } from '@mui/material'

import { AnchoredPanel } from 'components/UI/AnchoredPanel'
import { Search, SmartphoneNfc } from 'lucide-react'
import { border, font } from 'settings/tokens';
import { fieldSurfaceSx, FIELD_HEIGHT } from 'components/UI/fieldMetrics';
import { FieldTrigger } from 'components/UI/FieldTrigger';
import { Input } from 'components/UI/Input';
import { MENU_ROW_LABEL_SX, menuRowToneSx } from 'components/UI/menuRowTone';
import { certificateSubLabel } from 'utils/certificateUtils';

export type CertificatesSearchPickerProps = {
  mobile?: boolean
  open: boolean
  onOpen: () => void
  onClose: () => void
  query: string
  onQueryChange: (q: string) => void
  certificates: {
    name?: string
    id: string
    /** VRG-286 — the CHIP UID. Authenticated payloads only (VRG-231). */
    tag_uid?: string
    product: { type: string; title?: string; images?: string[]; sku?: string }
    status: string
    type: string
  }[]
  selectedIds: string[]
  onToggleId: (id: string, checked: boolean) => void
  onClear: () => void
  onOpenNfc?: () => void
}

export const CertificatesSearchPicker = ({
  mobile,
  open,
  onOpen,
  onClose,
  query,
  onQueryChange,
  certificates,
  selectedIds,
  onToggleId,
  onClear,
  onOpenNfc,
}: CertificatesSearchPickerProps) => {
  if (mobile) {
    return (
      <Stack direction={'row'} spacing={1} alignItems={'center'}>
        {/* Same trigger as the desktop branch below, and it was 44 while that one was
            40 — the same control, two heights, in one file. Both are `FieldTrigger`
            now, so they cannot disagree again. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FieldTrigger icon={<Search size={16} />} placeholder='Search' onClick={onOpen} />
        </Box>
        {/* Only where a scan can actually happen — the caller passes
            `onOpenNfc` only when `NDEFReader` exists (Chrome/Android).
            On iOS and desktop there is no Web NFC at all, so the control
            is absent rather than present-and-dead. */}
        {onOpenNfc && (
          <Button onClick={onOpenNfc} variant="text" sx={theme => ({ ...fieldSurfaceSx(theme), width: FIELD_HEIGHT, height: FIELD_HEIGHT, minWidth: 'auto', p: 0, '&:hover': { backgroundColor: border.subtle } })}>
            <SmartphoneNfc size={16} />
          </Button>
        )}
      </Stack>
    )
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Stack direction={'row'} spacing={1} alignItems={'center'}>
        {/* Read-only trigger — `FieldTrigger`, not a fifth copy of its surface. It was
            already a byte-for-byte reimplementation of it (same `fieldSurfaceSx`, same
            px, same gap, same 16px placeholder), minus the keyboard activation and the
            focus-visible outline the shared one carries. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FieldTrigger icon={<Search size={16} />} placeholder='Search' onClick={onOpen} />
        </Box>
        {/* Only where a scan can actually happen — the caller passes
            `onOpenNfc` only when `NDEFReader` exists (Chrome/Android).
            On iOS and desktop there is no Web NFC at all, so the control
            is absent rather than present-and-dead. */}
        {onOpenNfc && (
          <Button onClick={onOpenNfc} variant="text" sx={theme => ({ ...fieldSurfaceSx(theme), width: FIELD_HEIGHT, height: FIELD_HEIGHT, minWidth: 'auto', p: 0, '&:hover': { backgroundColor: border.subtle } })}>
            <SmartphoneNfc size={16} />
          </Button>
        )}
      </Stack>

      <AnchoredPanel open={open} onClickAway={onClose}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, borderRadius: '16px', border: '1px solid', borderColor: border.subtle, backgroundColor: '#ffffff', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <Stack spacing={1.5} sx={{ p: 1.5 }}>
              <Stack direction={'row'} spacing={1} alignItems={'center'}>
                {/* The hand-rolled version painted its `<input>` `#f5f5f5` INSIDE a
                    `grey[50]` surface — a second, slightly different grey rectangle
                    within the field — and hardcoded `#374151` for the text. Both are
                    gone on the base field, which also puts this popover's search on
                    exactly the inset of the `FieldTrigger` it opens over: the two sit
                    at the same screen position, so a 2px difference read as the
                    magnifier stepping sideways. */}
                <Input
                  value={query}
                  onChange={onQueryChange}
                  placeholder="Search"
                  startAdornment={<Search size={16} />}
                  inputProps={{ 'aria-label': 'Search certificates' }}
                />
                {/* Only where a scan can actually happen — the caller passes
                    `onOpenNfc` only when `NDEFReader` exists (Chrome/Android).
                    On iOS and desktop there is no Web NFC at all, so the control
                    is absent rather than present-and-dead. */}
                {onOpenNfc && (
                  <Button onClick={onOpenNfc} variant="text" sx={theme => ({ ...fieldSurfaceSx(theme), width: FIELD_HEIGHT, height: FIELD_HEIGHT, minWidth: 'auto', p: 0, '&:hover': { backgroundColor: border.subtle } })}>
                    <SmartphoneNfc size={16} />
                  </Button>
                )}
              </Stack>

              <Box sx={{ maxHeight: 460, overflowY: 'auto', pr: 0.5 }}>
                <Stack spacing={1.25}>
                  {certificates
                    .filter(c => ((c.product?.title ?? c.name ?? '') as string).toLowerCase().includes(query.toLowerCase()))
                    .map(cert => (
                      <Stack
                        key={cert.id}
                        direction={'row'}
                        alignItems={'center'}
                        spacing={1.25}
                        sx={theme => ({
                          px: 0.5,
                          // Selection stays the CHECKBOX tick — no selected background
                          // here. The tone module adds the rest of the contract: muted
                          // at rest, `text.primary` once ticked, accent + grey wash on
                          // hover/focus (this row had no hover state at all before).
                          ...menuRowToneSx(theme, { selected: selectedIds.includes(cert.id) }),
                        })}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(cert.id)}
                          onChange={(e) => onToggleId(cert.id, e.target.checked)}
                          icon={<Box sx={{ width: 22, height: 22, border: '1px solid', borderColor: '#374151', borderRadius: '6px', backgroundColor: theme => theme.palette.grey[50] }} />}
                          checkedIcon={<Box sx={{ width: 22, height: 22, borderRadius: '6px', backgroundColor: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Box component="span" sx={{ width: 12, height: 7, borderBottom: '2px solid #ffffff', borderLeft: '2px solid #ffffff', transform: 'rotate(-45deg)', mt: '-1px' }} /></Box>}
                          sx={{ p: 0, '& .MuiTouchRipple-root': { display: 'none' }, '&:hover': { backgroundColor: 'transparent' } }}
                        />
                        <Box sx={{ width: 36, height: 36, borderRadius: '8px', backgroundColor: 'gray.200', border: '1px solid', borderColor: border.subtle, overflow: 'hidden', flex: '0 0 36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {cert.product?.images?.[0] ? (
                            <img src={cert.product.images[0]} alt="certificate" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 40 40" fill="none">
                              <path fillRule="evenodd" clipRule="evenodd" d="M33 10H7V30H33V10ZM8.01961 29V11H31.9804V29H8.01961ZM14.6471 16C14.6471 17.1046 13.7341 18 12.6078 18C11.4816 18 10.5686 17.1046 10.5686 16C10.5686 14.8954 11.4816 14 12.6078 14C13.7341 14 14.6471 14.8954 14.6471 16ZM10.0588 27V24.5L14.6471 20L16.6863 22L23.8235 15L29.9412 21V27H10.0588Z" fill="#8B8D8F" opacity={0.5} />
                            </svg>
                          )}
                        </Box>
                        <Stack spacing={0}>
                          <Typography sx={{ ...MENU_ROW_LABEL_SX, fontSize: 14 }}>{cert.product?.title ?? cert.name ?? 'Unnamed certificate'}</Typography>
                          {/* Genuinely secondary, so it holds `text.secondary` through every
                              row state. VRG-231: was `sku ?? tag_uid ?? e_tag_id` — the chip
                              UID is never shown to a customer, so it now falls back to the
                              certificate's own short id instead. */}
                          <Typography sx={{ fontSize: font.label, color: 'text.secondary' }}>{certificateSubLabel(cert)}</Typography>
                        </Stack>
                      </Stack>
                    ))}
                </Stack>
              </Box>

              <Box sx={{ borderTop: `1px solid ${'gray.300'}`, pt: 1 }}>
                <Button variant="text" onClick={onClear} fullWidth sx={{ borderRadius: '12px', border: '1px solid', borderColor: border.subtle, height: 40, color: 'text.primary' }}>Clear</Button>
              </Box>
            </Stack>
          </Box>
      </AnchoredPanel>
    </Box>
  )
}

export default CertificatesSearchPicker


